import { useState } from "react"

import { UploadOutlined } from "@ant-design/icons"
import {
  Button as AntdButton,
  Col,
  Flex,
  Form,
  Input,
  message,
  Modal,
  Row,
  Typography,
  Upload
} from "antd"
import cls from "classname"
import { useTranslation } from "react-i18next"

import Images from "../../../../Images"
import PromocodeField from "../PromocodeField/PromocodeField"
import { PartnerModalActions } from "./PartnerModalActions"

import styles from "./PartnerModal.module.less"

const { Text, Title } = Typography
const { TextArea } = Input

const MAX_LOGO_SIZE_MB = 5
const ACCEPTED_LOGO_TYPES = ["image/png", "image/jpeg"]
const ACCEPTED_LOGO_EXTENSIONS = [".png", ".jpg", ".jpeg"]
const ACCEPTED_LOGO_LABEL = "*.png or *.jpeg"

const PARTNER_WEBSITE_PATTERN = /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/\S*)?$/i
const PARTNER_URL_PATTERN = /^https?:\/\/([\w-]+\.)+[\w-]{2,}(\/\S*)?$/i

const fakeRequest = ({ onSuccess }) => {
  setTimeout(() => onSuccess("ok"), 0)
}

const PartnerModal = ({ open, mode, initialValues, submitting, onSubmit, onCancel }) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const isEdit = mode === "edit"

  const [logoFile, setLogoFile] = useState(null)
  const [logoPreview, setLogoPreview] = useState(initialValues?.logo_url || null)
  const [logoError, setLogoError] = useState(false)

  const formInitialValues = {
    partner_name: initialValues?.partner_name,
    partner_website: initialValues?.partner_website,
    offer_details: initialValues?.offer_details,
    promocode: initialValues?.promocode,
    promocode_enabled: isEdit ? !!initialValues?.promocode : true,
    description: initialValues?.description,
    url: initialValues?.url
  }

  const beforeUpload = (file) => {
    if (!ACCEPTED_LOGO_TYPES.includes(file.type)) {
      message.error(t(`Only ${ACCEPTED_LOGO_LABEL} files are allowed`))
      return false
    }
    if (file.size / 1024 / 1024 > MAX_LOGO_SIZE_MB) {
      message.error(t(`Logo must be smaller than ${MAX_LOGO_SIZE_MB} MB`))
      return false
    }
    return true
  }

  const handleChangeUpload = (info) => {
    if (info.file.status === "done") {
      const file = info.file.originFileObj
      setLogoFile(file)
      setLogoPreview(URL.createObjectURL(file))
      setLogoError(false)
    }
  }

  const handleRemoveLogo = () => {
    setLogoFile(null)
    setLogoPreview(null)
  }

  const validateLogo = () => {
    const isValid = !!(logoFile || logoPreview)
    setLogoError(!isValid)
    return isValid
  }

  const handleFinishFailed = () => {
    validateLogo()
  }

  const handleFinish = ({ promocode_enabled, promocode, ...rest }) => {
    if (!validateLogo()) return
    const payload = promocode_enabled ? { ...rest, promocode } : rest

    const formData = new FormData()

    Object.entries(payload).forEach(([k, v]) => {
      if (v !== undefined) formData.append(k, v)
    })

    if (logoFile) formData.append("logo_file", logoFile)
    onSubmit(formData)
  }

  return (
    <Modal
      open={open}
      title={<Title level={4}>{isEdit ? t("Edit partner") : t("Add partner")}</Title>}
      onCancel={onCancel}
      footer={null}
      width={520}
      destroyOnHidden
      maskClosable={false}
      data-testid="partner-modal"
    >
      <Text type="secondary">
        {t("Create a new partner perk and assign it to eligible users.")}
      </Text>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        onFinishFailed={handleFinishFailed}
        initialValues={formInitialValues}
        requiredMark
        className={`with-default-error ${styles.form}`}
        disabled={submitting}
      >
        <div className={styles.logoField}>
          <div className={styles.logoLabel}>
            <Text strong>
              {t("Partner logo")} <Text type="danger">*</Text>
            </Text>
          </div>
          <Flex align="center" gap={12}>
            {logoPreview ? (
              <img
                src={logoPreview}
                alt="logo"
                className={cls(styles.logo, { [styles.logoError]: logoError })}
              />
            ) : (
              <Flex
                align="center"
                justify="center"
                className={cls(styles.logoPlaceholder, {
                  [styles.logoError]: logoError
                })}
              >
                <img src={Images.photo_placeholder} alt="" />
              </Flex>
            )}
            <Upload
              accept={ACCEPTED_LOGO_EXTENSIONS.join(",")}
              beforeUpload={beforeUpload}
              customRequest={fakeRequest}
              onChange={handleChangeUpload}
              showUploadList={false}
              maxCount={1}
              data-testid="partner-modal-logo-upload"
            >
              <AntdButton icon={<UploadOutlined />}>
                {/*{logoPreview ? t("Replace") : t("Click to Upload")}*/}
                {t("Click to Upload")}
              </AntdButton>
            </Upload>
            {logoPreview && <AntdButton onClick={handleRemoveLogo}>{t("Remove")}</AntdButton>}
          </Flex>
          <Flex vertical>
            <Text
              // type={logoError ? "danger" : "secondary"}
              type={"secondary"}
              className={styles.logoHint}
            >
              {t(
                `Upload ${ACCEPTED_LOGO_LABEL} files, up to ${MAX_LOGO_SIZE_MB}MB, recommended size is 400×400px.`
              )}
            </Text>
            {logoError && (
              <Text type="danger" className={"ant-form-item-explain-error"}>
                {"This field is required"}
              </Text>
            )}
          </Flex>
        </div>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              name="partner_name"
              label={t("Partner name")}
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: "This field is required"
                },
                {
                  max: 100,
                  message: "The field cannot exceed 100 characters"
                }
              ]}
            >
              <Input
                placeholder={"e.g Chargeback partner"}
                data-testid="partner-modal-name-input"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="partner_website"
              label={"Partner website"}
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: "This field is required"
                },
                {
                  pattern: PARTNER_WEBSITE_PATTERN,
                  message: "Enter a valid website URL"
                }
              ]}
            >
              <Input placeholder={"e.g chargeback.com"} data-testid="partner-modal-website-input" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="offer_details"
          label={"Offer details"}
          rules={[
            {
              required: true,
              whitespace: true,
              message: "This field is required"
            },
            { max: 150, message: "The field cannot exceed 150 characters" }
          ]}
        >
          <Input placeholder={"e.g 30% off annual plan"} data-testid="partner-modal-offer-input" />
        </Form.Item>

        <PromocodeField />

        <Form.Item
          name="description"
          label={"Description"}
          rules={[
            {
              required: true,
              whitespace: true,
              message: "This field is required"
            },
            {
              max: 1000,
              message: "Description cannot exceed 1,000 characters"
            }
          ]}
        >
          <TextArea
            rows={4}
            placeholder={"Enter any additional or related information to partner…"}
            data-testid="partner-modal-description-input"
          />
        </Form.Item>

        <Form.Item
          name="url"
          label={"URL"}
          rules={[
            { required: true, message: "This field is required" },
            {
              pattern: PARTNER_URL_PATTERN,
              message: "Enter a valid URL"
            }
          ]}
        >
          <Input placeholder="https://" data-testid="partner-modal-url-input" />
        </Form.Item>

        <PartnerModalActions
          submitting={submitting}
          onCancel={onCancel}
          hasLogo={logoPreview}
          isEdit={isEdit}
          form={form}
        />
      </Form>
    </Modal>
  )
}

export default PartnerModal
